'use client'

import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { Link } from '@/i18n/navigation'
import { ComponentProps, useEffect, useId, useRef, useState } from 'react'
import ListCard, { ListCardType } from './ListCard'
import { cn } from '@/lib/utils'

export default function CardCarousel({
  title,
  items,
  link,
  linkTitle,
}: {
  title?: string
  items: ListCardType[]
  link?: ComponentProps<typeof Link>['href']
  linkTitle?: string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const titleId = useId()

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardRefs.current.findIndex((ref) => ref === entry.target)
            if (index !== -1) {
              setCurrentIndex(index)
            }
          }
        })
      },
      {
        root: scrollRef.current,
        threshold: 0.5, // Card is considered "current" when 50% visible
        rootMargin: '0px',
      },
    )

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [items.length])

  return (
    <div ref={scrollRef} className='relative flex flex-col space-y-4'>
      {title && <h3 id={titleId}>{title}</h3>}

      <Carousel
        aria-labelledby={title ? titleId : undefined}
        plugins={[WheelGesturesPlugin()]}
        className='overflow-x-hidden'
        opts={{
          align: 'start',
          dragFree: true,
          containScroll: 'trimSnaps',
        }}
      >
        <CarouselContent
          className={cn('relative h-full pr-12 pb-4', { 'pr-0': items.length <= 3 })}
        >
          {items.map((item, index) => (
            <CarouselItem
              ref={(el) => {
                cardRefs.current[index] = el
              }}
              key={index}
              className={cn('sm:basis-1/2 md:basis-1/3', {
                'md:basis-1/3': items.length <= 3,
              })}
            >
              <ListCard {...item} className='w-full' loading='eager' />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className='flex w-full justify-center md:hidden' aria-hidden='true'>
        {items.length > 1 &&
          items.map((_, index) => (
            <span
              key={index}
              className={cn('mx-1 my-4 h-2 w-2 rounded-full transition-all duration-300 ease-out', {
                'w-4 bg-stone-600': index === currentIndex,
                'bg-stone-300': index !== currentIndex,
              })}
            />
          ))}
      </div>

      {link && linkTitle && (
        <div className='flex justify-center'>
          <Link href={link} className='w-fit rounded-full border px-4 py-2'>
            {linkTitle}
          </Link>
        </div>
      )}
    </div>
  )
}
